import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { ApiError } from '../../../lib/api/_request.js';
import { alignRow, availableRefIds, bdCell, emptyRow, isBlankRow } from './bd-defs.js';
import {
  duplicateSentRow,
  fieldErrorKey,
  placeSaveErrors,
  removeSentRow,
  saveIssues,
} from './bd-presentation.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdColumnDef,
  BdIssue,
  BdRow,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';
import type { TFunction } from 'i18next';

/** 保存が止まった理由。先頭の文と、指摘の並び。 @graph-connects none */
interface BdSaveFailure {
  lead: string;
  issues: BdIssue[];
}

/**
 * 保存できなかった理由を人が読む形にする。API が指摘の並びを返したときは行と欄に振り分ける。
 * @graph-connects saveIssues [calls] 指摘の並びの取り出し
 */
function failureOf(error: unknown, t: TFunction): BdSaveFailure | null {
  if (error === null || error === undefined) return null;
  if (!(error instanceof ApiError)) {
    return { lead: error instanceof Error ? error.message : t('bd.save.failed'), issues: [] };
  }
  if (error.status === 403) return { lead: t('bd.save.forbidden'), issues: [] };
  const issues = saveIssues(error.details).filter((issue) => issue.severity === 'error');
  return issues.length === 0
    ? { lead: error.message, issues: [] }
    : { lead: t('bd.save.fixBelow'), issues };
}

/** 欄の幅を 4 列の格子の何列ぶんにするか。 @graph-connects none */
const WIDTH_CLASS: Record<BdColumnDef['width'], string> = {
  full: 'md:col-span-4',
  half: 'md:col-span-2',
  quarter: 'md:col-span-1',
};

/** 指せる ID を手がかりに出す数の上限。多すぎると欄の下が埋まる。 @graph-connects none */
const REF_HINT_LIMIT = 20;

export interface BdRowsEditorProps {
  /** 編集する節の定義（列・選択肢・上限・手がかり） */
  def: BdArtifactDef;
  /** 保存済みの節。参照の列で指せる ID の手がかりに使う */
  saved: Partial<Record<BdArtifact, Pick<BdSavedArtifact, 'rows'>>>;
  /** 今保存されている行。まだ無い節では null */
  savedRows: BdRow[] | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因。上限・選択肢・参照・記入例はここに API の指摘が入る */
  error: unknown;
  /** 保存が通ったときに残った注意 */
  warnings: string[];
  /** 保存する。全部空の行は除いて渡す */
  onSave: (rows: BdRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** 任意の節でまだ行が無いときだけ渡す。0 行（該当なし）で保存する */
  onNotApplicable?: () => void;
}

/**
 * 基本設計の節の行を、1 行 1 枚のカードで編集する。欄は API が返す節の定義から組み立てる。
 *
 * 1 行の表に詰めると文章の欄が細くなって読めないので、1 行をカードにして、欄ごとに見出しと
 * 書き方の手がかりを出す。行 ID の欄は画面が振り（接頭辞 + 今ある最大の番号 + 1）、書き換え
 * させない。参照の欄は自由に書けるが、指せる ID を手がかりに並べる。
 *
 * 上限・空欄・選択肢・参照・記入例の判定は API が持つ（MCP の check と同じ規則）。画面は欄の
 * 今の文字数だけを出し、保存で止まった理由は指摘の行・列から、その行のカードとその欄の下に出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の行を、定義から組み立てた欄（行 ID は自動、選ぶ欄は選択肢、参照は指せる ID の手がかりつき）で編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 * @graph-connects failureOf [calls] 保存できなかった理由の組み立て
 * @graph-connects placeSaveErrors [calls] 理由を欄・行のカード・フォームの下に振り分ける
 * @graph-connects BdField [calls] 欄 1 つの描画
 */
export function BdRowsEditor({
  def,
  saved,
  savedRows,
  saving,
  error,
  warnings,
  onSave,
  onCancel,
  onNotApplicable,
}: BdRowsEditorProps) {
  const { t } = useTranslation();
  const formId = useId();
  const initial = savedRows ?? [];
  const [rows, setRows] = useState<BdRow[]>(() =>
    initial.length === 0 ? [emptyRow(def, [])] : initial.map((r) => alignRow(def, r)),
  );
  const [sentRows, setSentRows] = useState<(number | null)[]>([]);

  // 編集中に消した行の番号も使わないよう、保存済みの行も数えて次の ID を決める
  const nextRow = (current: readonly BdRow[]): BdRow => emptyRow(def, [...initial, ...current]);

  const setCell = (index: number, key: string, value: string) => {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };

  const failure = failureOf(error, t);
  const placed = placeSaveErrors(failure?.issues ?? [], sentRows);
  // 理由がどれも消した行のものなら、もう直すところは無い
  const showFailure =
    failure !== null &&
    (failure.issues.length === 0 || placed.general.length > 0 || placed.rows.length > 0);
  const rowLabel = (index: number): string => t('bd.editor.row', { n: index + 1 });

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        // 全部空の行は送らない（API は行数で数えるので、空行が上限を食う）
        const sent = rows.map((r, i) => ({ r, i })).filter(({ r }) => !isBlankRow(def, r));
        setSentRows(sent.map(({ i }) => i));
        onSave(sent.map(({ r }) => alignRow(def, r)));
      }}
    >
      <ol className="space-y-3" aria-label={t('bd.editor.rowsAria')}>
        {rows.map((row, index) => {
          const rowMessages = placed.byRow.get(index);
          const flagged = placed.rows.includes(index);
          const id = bdCell(row, 'id');
          return (
            // 欄はどれも rows から値を入れるので、index を鍵にしても複製・削除で中身はずれない
            <li key={index}>
              <Card padding="sm" className={flagged ? 'border-[var(--color-error)]' : undefined}>
                <CardContent className="mt-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-[var(--color-text-secondary)]">
                      {rowLabel(index)}
                      {def.idPrefix === null || id === '' ? null : (
                        <code className="ml-2 rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs">
                          {id}
                        </code>
                      )}
                    </h3>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('bd.editor.duplicateAria', { n: index + 1 })}
                        onClick={() => {
                          setSentRows((current) => duplicateSentRow(current, index));
                          setRows((current) => {
                            const copy =
                              def.idPrefix === null
                                ? { ...row }
                                : { ...row, id: bdCell(nextRow(current), 'id') };
                            return [
                              ...current.slice(0, index + 1),
                              copy,
                              ...current.slice(index + 1),
                            ];
                          });
                        }}
                      >
                        {t('bd.editor.duplicate')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:text-[var(--color-error-text)]"
                        aria-label={t('bd.editor.removeAria', { n: index + 1 })}
                        onClick={() => {
                          setSentRows((current) => removeSentRow(current, index));
                          setRows((current) => current.filter((_, i) => i !== index));
                        }}
                      >
                        {t('bd.editor.remove')}
                      </Button>
                    </div>
                  </div>

                  {rowMessages === undefined ? null : (
                    <ul
                      aria-label={t('bd.editor.rowIssuesAria', { n: index + 1 })}
                      className="mt-2 list-disc pl-5 text-xs text-[var(--color-error-text)]"
                    >
                      {rowMessages.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-4">
                    {def.columns.map((column) => (
                      <BdField
                        key={column.key}
                        id={`${formId}-${String(index)}-${column.key}`}
                        rowNumber={index + 1}
                        column={column}
                        value={bdCell(row, column.key)}
                        errors={placed.byField.get(fieldErrorKey(index, column.key)) ?? []}
                        refIds={
                          column.kind === 'ref' || column.kind === 'refs'
                            ? availableRefIds({ column, def, formRows: rows, ownId: id, saved })
                            : []
                        }
                        onChange={(value) => {
                          setCell(index, column.key, value);
                        }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ol>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          setRows((current) => [...current, nextRow(current)]);
        }}
      >
        {t('bd.editor.addRow')}
      </Button>

      {failure === null || !showFailure ? null : (
        <div
          role="alert"
          className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error-muted)] px-4 py-3 text-sm text-[var(--color-error-text)]"
        >
          <p className="whitespace-pre-wrap">{failure.lead}</p>
          {placed.general.length === 0 && placed.rows.length === 0 ? null : (
            <ul className="mt-1 list-disc pl-5">
              {placed.general.map((item) => (
                <li key={item}>{item}</li>
              ))}
              {placed.rows.length === 0 ? null : (
                <li>
                  {t('bd.editor.fixRows', {
                    rows: placed.rows.map(rowLabel).join(t('bd.editor.rowSeparator')),
                  })}
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {warnings.length === 0 ? null : (
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning-muted)] px-4 py-3 text-sm">
          <p className="font-semibold text-[var(--color-warning-text)]">
            {t('bd.editor.warnings')}
          </p>
          <ul className="mt-1 list-disc pl-5 text-[var(--color-text-primary)]">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-4">
        <Button type="submit" loading={saving} disabled={saving}>
          {saving ? t('bd.editor.saving') : t('bd.editor.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('bd.editor.cancel')}
        </Button>
        {onNotApplicable === undefined ? null : (
          <Button
            type="button"
            variant="ghost"
            className="ml-auto"
            disabled={saving}
            onClick={onNotApplicable}
          >
            {t('bd.section.markNotApplicable')}
          </Button>
        )}
      </div>
    </form>
  );
}

interface BdFieldProps {
  /** 入力欄の id（見出しの label と結ぶ） */
  id: string;
  /** 行番号（1 から）。読み上げ名に使う */
  rowNumber: number;
  column: BdColumnDef;
  value: string;
  /** この欄で保存が止まった理由 */
  errors: string[];
  /** 参照の欄で指せる ID */
  refIds: string[];
  onChange: (value: string) => void;
}

/**
 * 欄 1 つ。行 ID は書き換えられない欄、選ぶ欄は選択肢、ほかは文章の欄にする。
 * @graph-connects none
 */
function BdField({ id, rowNumber, column, value, errors, refIds, onChange }: BdFieldProps) {
  const { t } = useTranslation();
  const ariaLabel = t('bd.editor.fieldAria', { n: rowNumber, label: column.label });
  const invalid = errors.length > 0;
  const isRef = column.kind === 'ref' || column.kind === 'refs';
  const shownIds = refIds.slice(0, REF_HINT_LIMIT);
  const moreIds = refIds.length - shownIds.length;
  const over = column.limit !== undefined && value.length > column.limit;

  return (
    <div className={WIDTH_CLASS[column.width]}>
      <label
        htmlFor={id}
        className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]"
      >
        {column.label}
        {column.required && column.kind !== 'id' ? (
          <span className="ml-0.5 text-[var(--color-error-text)]" title={t('bd.required')}>
            *
          </span>
        ) : null}
      </label>
      {column.kind === 'id' ? (
        <Input
          id={id}
          size="sm"
          fullWidth
          readOnly
          aria-label={ariaLabel}
          aria-describedby={`${id}-hint`}
          value={value}
        />
      ) : column.kind === 'choice' ? (
        <Select
          id={id}
          size="sm"
          fullWidth
          aria-label={ariaLabel}
          aria-invalid={invalid}
          placeholder={t('bd.editor.choose')}
          options={column.choices ?? []}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ) : (
        <Textarea
          id={id}
          rows={column.width === 'full' && column.kind === 'text' ? 2 : 1}
          className={`px-3 py-1.5 ${invalid ? 'border-[var(--color-error)]' : ''}`}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          placeholder={column.placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      )}
      <div
        id={`${id}-hint`}
        className="mt-1 flex justify-between gap-2 text-xs text-[var(--color-text-muted)]"
      >
        <span className="min-w-0">
          {column.kind === 'id' ? t('bd.editor.idAuto') : (column.hint ?? '')}
          {isRef ? (
            <span className="block">
              {refIds.length === 0
                ? t('bd.editor.noAvailableIds')
                : t('bd.editor.availableIds', {
                    ids:
                      moreIds > 0
                        ? `${shownIds.join(', ')} ${t('bd.editor.moreIds', { count: moreIds })}`
                        : shownIds.join(', '),
                  })}
            </span>
          ) : null}
        </span>
        {column.limit === undefined || column.kind === 'id' || column.kind === 'choice' ? null : (
          <span className={`shrink-0 tabular-nums ${over ? 'text-[var(--color-error-text)]' : ''}`}>
            {t('bd.editor.chars', { n: value.length, limit: column.limit })}
          </span>
        )}
      </div>
      {errors.length === 0 ? null : (
        <ul
          aria-label={t('bd.editor.fieldIssuesAria', { n: rowNumber, label: column.label })}
          className="mt-1 list-disc pl-5 text-xs text-[var(--color-error-text)]"
        >
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
